// frontend/src/components/ui/index.jsx
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

/* ── Button ──────────────────────────────────────────────────── */
export function Button({ children, variant = 'primary', size = 'md', loading, disabled, className, as: Component = 'button', ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg-surface disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary:   'bg-cyan text-bg hover:bg-cyan-light focus:ring-cyan shadow-glow-cyan',
    secondary: 'bg-bg-elevated text-text-primary border border-border hover:border-border-hi',
    outline:   'bg-transparent border border-cyan text-cyan hover:bg-cyan/10',
    danger:    'bg-red text-white hover:bg-red-dark focus:ring-red',
    ghost:     'bg-transparent text-text-secondary hover:bg-bg-elevated hover:text-text-primary',
    success:   'bg-green text-white hover:bg-green-dark focus:ring-green',
    purple:    'bg-purple text-white hover:bg-purple-dark focus:ring-purple shadow-glow-purple',
  };

  const sizes = {
    xs: 'px-3 py-1.5 text-xs',
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg',
  };

  return (
    <Component
      className={clsx(base, variants[variant], sizes[size], className)}
      disabled={Component === 'button' ? (disabled || loading) : undefined}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </Component>
  );
}

/* ── Card ────────────────────────────────────────────────────── */
export function Card({ children, className, accent, noPad, ...props }) {
  return (
    <div
      className={clsx(
        'bg-bg-surface border border-border rounded-2xl relative overflow-hidden',
        !noPad && 'p-5',
        accent && `border-t-2`,
        className
      )}
      style={accent ? { borderTopColor: accent } : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Input ───────────────────────────────────────────────────── */
export function Input({ label, error, note, className, ...props }) {
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label && <label className="text-xs font-bold text-text-muted uppercase tracking-wider">{label}</label>}
      <input
        className={clsx(
          'w-full bg-bg-panel border rounded-xl px-4 py-3 text-text-primary text-sm',
          'placeholder:text-text-muted outline-none transition-colors',
          'focus:border-cyan focus:ring-1 focus:ring-cyan/30',
          error ? 'border-red' : 'border-border hover:border-border-hi'
        )}
        {...props}
      />
      {error && <span className="text-xs text-red">{error}</span>}
      {note && <span className="text-xs text-text-muted">{note}</span>}
    </div>
  );
}

/* ── Select ──────────────────────────────────────────────────── */
export function Select({ label, error, className, children, ...props }) {
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label && <label className="text-xs font-bold text-text-muted uppercase tracking-wider">{label}</label>}
      <select
        className={clsx(
          'w-full bg-bg-panel border rounded-xl px-4 py-3 text-text-primary text-sm',
          'outline-none transition-colors focus:border-cyan',
          error ? 'border-red' : 'border-border'
        )}
        {...props}
      >
        {children}
      </select>
      {error && <span className="text-xs text-red">{error}</span>}
    </div>
  );
}

/* ── Badge ───────────────────────────────────────────────────── */
export function Badge({ label, color = 'cyan', size = 'sm' }) {
  const colorMap = {
    cyan:   'bg-cyan/15 text-cyan border-cyan/30',
    green:  'bg-green/15 text-green border-green/30',
    amber:  'bg-amber/15 text-amber border-amber/30',
    red:    'bg-red/15 text-red border-red/30',
    purple: 'bg-purple/15 text-purple border-purple/30',
    blue:   'bg-blue/15 text-blue border-blue/30',
    gray:   'bg-bg-elevated text-text-muted border-border',
    gold:   'bg-gold/15 text-gold border-gold/30',
    teal:   'bg-teal/15 text-teal border-teal/30',
    rose:   'bg-rose/15 text-rose border-rose/30',
  };
  return (
    <span className={clsx(
      'inline-flex items-center border rounded-lg font-bold uppercase tracking-wider whitespace-nowrap',
      colorMap[color] || colorMap.gray,
      size === 'sm' ? 'text-[10px] px-2 py-0.5' : 'text-xs px-3 py-1'
    )}>{label}</span>
  );
}

/* ── Progress Bar ────────────────────────────────────────────── */
export function ProgressBar({ pct, color = '#00A8DB', label, value, thin }) {
  return (
    <div className="w-full">
      {(label || value) && (
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-text-secondary">{label}</span>
          <span className="font-bold" style={{ color }}>{value}</span>
        </div>
      )}
      <div className={clsx('w-full bg-border rounded-full overflow-hidden', thin ? 'h-1' : 'h-1.5')}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

/* ── Loading Spinner ─────────────────────────────────────────── */
export function Spinner({ size = 'md', className }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-12 h-12' };
  return <Loader2 className={clsx('animate-spin text-cyan', sizes[size], className)} />;
}

/* ── Empty State ─────────────────────────────────────────────── */
export function Empty({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {icon && <div className="text-5xl mb-4">{icon}</div>}
      <h3 className="text-text-primary font-semibold text-base mb-2">{title}</h3>
      {description && <p className="text-text-muted text-sm mb-6 max-w-xs">{description}</p>}
      {action}
    </div>
  );
}

/* ── Stat Card ───────────────────────────────────────────────── */
export function StatCard({ label, value, sub, icon, color = '#00A8DB', sparkData, loading }) {
  return (
    <Card accent={color} className="flex flex-col gap-3">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1.5">{label}</p>
          {loading
            ? <div className="h-7 w-24 bg-bg-elevated rounded animate-pulse" />
            : <p className="text-[22px] font-black text-text-primary leading-none tracking-tight">{value}</p>
          }
          {sub && !loading && (
            <p className={clsx('text-xs font-semibold mt-1.5',
              sub.startsWith('↑') || sub.startsWith('+') ? 'text-green' :
              sub.startsWith('↓') || sub.startsWith('-') ? 'text-red' : 'text-text-secondary'
            )}>{sub}</p>
          )}
        </div>
        {icon && <span className="text-2xl opacity-60">{icon}</span>}
      </div>
      {sparkData && <MiniSparkline data={sparkData} color={color} />}
    </Card>
  );
}

/* ── Mini Sparkline (pure SVG) ───────────────────────────────── */
export function MiniSparkline({ data, color = '#00A8DB', height = 36 }) {
  if (!data || data.length < 2) return null;
  const mn = Math.min(...data), mx = Math.max(...data), range = mx - mn || 1;
  const w = 100, h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - mn) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const gid = `sp${color.replace(/[^a-z0-9]/gi, '')}${height}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gid})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Modal ───────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, children, size = 'md' }) {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative w-full bg-bg-surface border border-border rounded-2xl shadow-card animate-slide-up flex flex-col max-h-[90vh]', sizes[size])}>
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
          <h3 className="text-text-primary font-bold text-base">{title}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xl font-bold leading-none">×</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

/* ── Table ───────────────────────────────────────────────────── */
export function Table({ headers, children, className }) {
  return (
    <div className={clsx('overflow-x-auto', className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            {headers.map((h, i) => (
              <th key={i} className="text-left text-[10px] font-bold text-text-muted uppercase tracking-wider pb-3 pr-4">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/30">{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, className }) {
  return <td className={clsx('py-3 pr-4 text-sm text-text-secondary', className)}>{children}</td>;
}

/* ── Section Header ──────────────────────────────────────────── */
export function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-text-primary font-bold text-sm tracking-tight">{title}</h3>
      {action}
    </div>
  );
}
